import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import {
    Game,
    Upload,
    UploadStatus,
    UploadType,
    User,
} from '../../../workers/ApiWorker';
import { CardContent } from '@mui/material';
import { useCustomTheme } from '../../../contexts/ThemeContext';
import Box from '@mui/material/Box';
import {
    Cancel,
    HourglassTop,
    NewReleases,
    Pending,
    VisibilityOff,
} from '@mui/icons-material';
import {
    OrangeColor,
    TextCardPreviewDescriptionColor,
} from '../../../helpers/Themes';
import NoImage from '../../upload/NoImage';
import {
    calculateTimeLeft,
    getUploadStatus,
    hasOpenReports,
    isUploadPublic,
    removalDay,
    renderTimeLeft,
    TimeLeftType,
} from '../../../helpers/Uploads';
import PreviewCardStatusBanner from './PreviewCardStatusBanner';
import PatreonIcon from '../icons/PatreonIcon';

// Defined OUTSIDE PreviewCard so React never remounts children on re-render
interface CardWrapperProps {
    enableLink: boolean;
    linkTo: string;
    onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    children: React.ReactNode;
}

const CardWrapper: React.FC<CardWrapperProps> = ({
    enableLink,
    linkTo,
    onClick,
    children,
}) => {
    if (enableLink) {
        return (
            <Link
                to={linkTo}
                onClick={onClick}
                style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'block',
                }}
            >
                {children}
            </Link>
        );
    }
    return <div>{children}</div>;
};

interface PreviewCardProps {
    uuid?: string;
    username?: string;
    title: string;
    description: string;
    image: string | null;
    games: Game[];
    type: UploadType;
    upload?: Upload;
    user?: User;
    handleReuseDetails?: (upload: Upload) => void;
    enableLink?: boolean;
    galleryImages?: string[];
}

const PreviewCard: React.FC<PreviewCardProps> = ({
    uuid,
    username,
    title,
    description,
    image,
    games,
    type,
    upload,
    user,
    handleReuseDetails,
    enableLink = false,
    galleryImages: galleryImagesProp,
}) => {
    const [imgError, setImgError] = useState(false);
    const { theme } = useCustomTheme();

    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const galleryImages = galleryImagesProp ?? upload?.images?.gallery ?? [];
    const totalImages = 1 + galleryImages.length;

    useEffect(() => {
        if (galleryImages.length === 0) {
            setCurrentImageIndex(0);
            return;
        }

        const interval = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % totalImages);
        }, 3000);

        return () => clearInterval(interval);
    }, [galleryImages.length, totalImages]);

    const [timeLeftForRelease, setTimeLeftForRelease] =
        useState<TimeLeftType>('');

    useEffect(() => {
        if (upload) {
            let scheduledTime: number | undefined = undefined;
            const latestVersion = upload.versions[upload.versions.length - 1];

            switch (getUploadStatus(upload)) {
                case UploadStatus.EARLYACCESS:
                    if (latestVersion.scheduledAtPublic) {
                        scheduledTime = Date.parse(
                            latestVersion.scheduledAtPublic
                        );
                    }
                    break;
                case UploadStatus.COMINGSOON_PUBLIC:
                    if (latestVersion.scheduledAtPublic) {
                        scheduledTime = Date.parse(
                            latestVersion.scheduledAtPublic
                        );
                    }
                    break;
                case UploadStatus.COMINGSOON_PRIVATE:
                    if (latestVersion.scheduledAtPrivate) {
                        scheduledTime = Date.parse(
                            latestVersion.scheduledAtPrivate
                        );
                    }
                    break;
                case UploadStatus.COMINGSOON_EARLYACCESS:
                    if (latestVersion.scheduledAtPrivate) {
                        scheduledTime = Date.parse(
                            latestVersion.scheduledAtPrivate
                        );
                    }
                    break;
            }

            if (scheduledTime) {
                const interval = setInterval(() => {
                    const remainingTime = calculateTimeLeft(scheduledTime);
                    setTimeLeftForRelease(remainingTime);

                    if (remainingTime === null) {
                        clearInterval(interval);
                    }
                }, 1000);

                return () => clearInterval(interval);
            }
        }
    }, [upload]);

    const linkTo = uuid && username ? `/${username}/${uuid}` : '#';

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (handleReuseDetails && upload) {
            e.preventDefault();
            handleReuseDetails(upload);
        }
    };

    return (
        <Card
            sx={{
                cursor: uuid ? 'pointer' : undefined,
                transition: 'transform 0.2s',
                '&:hover': {
                    filter: 'brightness(1.1)',
                    transition: 'all 0.06s ease',
                },
            }}
        >
            <CardWrapper
                enableLink={enableLink}
                linkTo={linkTo}
                onClick={handleClick}
            >
                {/* Status Banners */}
                {upload ? (
                    getUploadStatus(upload) === UploadStatus.PENDING ? (
                        <PreviewCardStatusBanner
                            colorDark={theme.palette.warning.main}
                            colorMain={theme.palette.warning.main}
                            icon={<Pending />}
                            status={'Pending'}
                            information={'Under Review'}
                        />
                    ) : getUploadStatus(upload) === UploadStatus.REJECTED ? (
                        <PreviewCardStatusBanner
                            colorDark={theme.palette.error.main}
                            colorMain={theme.palette.error.main}
                            icon={<Cancel />}
                            status={'Rejected'}
                            information={
                                upload.declined
                                    ? removalDay(upload.declined.declinedAt)
                                    : ''
                            }
                        />
                    ) : hasOpenReports(upload, user) ? (
                        <PreviewCardStatusBanner
                            colorDark={theme.palette.error.main}
                            colorMain={theme.palette.error.main}
                            icon={<NewReleases />}
                            status={'Reported'}
                            information={upload.pendingReports + ' Reports'}
                        />
                    ) : getUploadStatus(upload) ===
                      UploadStatus.COMINGSOON_PUBLIC ? (
                        <PreviewCardStatusBanner
                            colorDark={theme.palette.warning.main}
                            colorMain={theme.palette.warning.main}
                            icon={<HourglassTop />}
                            status={'Releasing'}
                            information={renderTimeLeft(timeLeftForRelease)}
                        />
                    ) : getUploadStatus(upload) ===
                      UploadStatus.COMINGSOON_PRIVATE ? (
                        <PreviewCardStatusBanner
                            colorDark={OrangeColor}
                            colorMain={OrangeColor}
                            icon={<HourglassTop />}
                            status={'Releasing'}
                            information={renderTimeLeft(timeLeftForRelease)}
                        />
                    ) : getUploadStatus(upload) === UploadStatus.EARLYACCESS ? (
                        <PreviewCardStatusBanner
                            colorDark={theme.palette.warning.main}
                            colorMain={theme.palette.warning.main}
                            icon={<HourglassTop />}
                            status={'Public Release'}
                            information={renderTimeLeft(timeLeftForRelease)}
                        />
                    ) : getUploadStatus(upload) ===
                      UploadStatus.COMINGSOON_EARLYACCESS ? (
                        <PreviewCardStatusBanner
                            colorDark={OrangeColor}
                            colorMain={OrangeColor}
                            icon={<HourglassTop />}
                            status={'Early Access'}
                            information={renderTimeLeft(timeLeftForRelease)}
                        />
                    ) : null
                ) : null}

                {/* Patreon Icon */}
                {upload && !isUploadPublic(upload) ? (
                    <Box sx={{ position: 'relative' }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                padding: '6px 6px',
                                borderRadius: '4px',
                                zIndex: 10,
                            }}
                        >
                            <PatreonIcon />
                        </Box>
                    </Box>
                ) : null}

                {/* Hidden Until Icon */}
                {upload && upload.hiddenUntil ? (
                    <Box sx={{ position: 'relative' }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                padding: '6px 6px',
                                borderRadius: '4px',
                                zIndex: 10,
                            }}
                        >
                            <VisibilityOff />
                        </Box>
                    </Box>
                ) : null}

                {/* Card Image */}
                {!imgError && image ? (
                    <div style={{ position: 'relative', height: 210 }}>
                        <CardMedia
                            sx={{
                                height: 210,
                                objectPosition:
                                    type.name === 'arena' ? 'center' : 'top',
                                position: 'absolute',
                                opacity: currentImageIndex === 0 ? 1 : 0,
                                transition: 'opacity 1s ease-in-out',
                            }}
                            component="img"
                            alt={title}
                            height="100%"
                            image={image}
                            onError={() => setImgError(true)}
                        />
                        {galleryImages.map((src, i) => (
                            <CardMedia
                                key={src}
                                sx={{
                                    height: 210,
                                    objectPosition:
                                        type.name === 'arena'
                                            ? 'center'
                                            : 'top',
                                    position: 'absolute',
                                    opacity:
                                        currentImageIndex === i + 1 ? 1 : 0,
                                    transition: 'opacity 1s ease-in-out',
                                }}
                                component="img"
                                alt={title}
                                height="100%"
                                image={src}
                                onError={() => setImgError(true)}
                            />
                        ))}
                    </div>
                ) : (
                    <CardMedia sx={{ height: 210 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                            }}
                        >
                            <NoImage />
                        </Box>
                    </CardMedia>
                )}

                {/* Card Content */}
                <CardContent
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        backgroundColor: theme.palette.secondary.main,
                        height: '105px',
                    }}
                >
                    {/* Blurred background image */}
                    {!imgError && image && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: 'full',
                                overflow: 'hidden',
                                backgroundColor: theme.palette.secondary.light,
                            }}
                        >
                            <img
                                src={image}
                                alt="blurred background"
                                style={{
                                    width: '538px',
                                    height: '286px',
                                    margin: '-45px 2px',
                                    filter: 'blur(40px)',
                                    opacity: 0.2,
                                }}
                            />
                        </Box>
                    )}

                    <div style={{ position: 'relative', zIndex: 2 }}>
                        <Typography
                            textAlign={'left'}
                            variant="caption"
                            sx={{
                                color: theme.palette.warning.main,
                                display: 'block',
                                textAlign: 'left',
                                marginBottom: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <>
                                {games.length > 0 &&
                                    games[0].name.toUpperCase()}
                            </>
                        </Typography>
                        <Typography
                            textAlign={'left'}
                            variant="h5"
                            sx={{
                                marginBottom: 1,
                                marginTop: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                            }}
                        >
                            {title.toUpperCase()}
                        </Typography>
                        <Typography
                            textAlign={'left'}
                            variant="body2"
                            fontSize={'14px'}
                            color={TextCardPreviewDescriptionColor}
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                            }}
                        >
                            {description}
                        </Typography>
                    </div>
                </CardContent>
            </CardWrapper>
        </Card>
    );
};

export default PreviewCard;
